<template>
  <ToastProvider v-if="toasts.length > 0">
    <ToastViewport
      class="[--viewport-padding:_25px] fixed toast-viewport right-4 flex flex-col p-[var(--viewport-padding)] gap-[10px] w-[390px] max-w-[100vw] m-0 list-none z-[2147483647] outline-none"
    />
    <ToastRoot
      v-for="toast in toasts"
      :key="toast.id"
      :show="toast.show"
      :duration="toast.duration"
      :type="toast.type"
      @update:open="openStateChanged(toast.id!, $event)"
      class="custom-toast bg-white rounded-lg shadow-sm border p-[15px] grid [grid-template-areas:_'title_action'_'description_action'] grid-cols-[auto_max-content] gap-x-[15px] items-center data-[swipe=move]:translate-x-[var(--reka-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut"
      :class="{ 'toast-fade': toast.animation === 'fadeIn' || toast.animation === 'fadeOut', 'border-[#CF2402]': toast.error }"
    >
      <ToastTitle v-if="toast.title" class="[grid-area:_title] mb-[5px] font-medium text-slate12 text-sm">
        {{ toast.title }}
      </ToastTitle>
      <ToastDescription
        :class="{ 'mb-1': !!!toast.title, 'm-0': !!toast.title, 'cursor-pointer': !!toast.onClickAction }"
        class="[grid-area:_description] text-slate11 text-xs leading-[1.3]"
        @click="toast.onClickAction?.()"
      >
        {{ toast.message }}
      </ToastDescription>
      <ToastClose aria-label="Close" class="[grid-area:_action] self-start">
        <span aria-hidden="true">×</span>
      </ToastClose>
    </ToastRoot>
  </ToastProvider>
</template>

<script setup lang="ts">
  import { ToastProvider, ToastRoot, ToastViewport, ToastTitle, ToastDescription, ToastClose } from 'reka-ui';

  export interface Toast {
    show: boolean;
    id?: string;
    duration?: number;
    type: 'foreground' | 'background';
    animation?: 'slideIn' | 'slideOut' | 'fadeIn' | 'fadeOut';
    title?: string;
    message: string;
    error?: boolean;
    onClickAction?: () => void;
  }

  const toasts = ref<Toast[]>([]);

  const idCounter = ref(0);
  const addToast = (toast: Toast) => {
    // set up default values
    toast.id = toast.id || idCounter.value.toString();
    idCounter.value++;

    toast.duration = toast.duration || 3000;
    toast.type = toast.type || 'foreground';
    toast.animation = toast.animation || 'slideIn';
    toast.show = true;

    toasts.value.push(toast);
  };

  const delayedRemoveToast = (id: string, duration?: number) => {
    setTimeout(() => {
      toasts.value = toasts.value.filter(toast => toast.id !== id);
    }, duration || 3000);
  };

  const openStateChanged = (id: string, open: boolean) => {
    if (!open) {
      delayedRemoveToast(id);
    }
  };

  const eventBus = useEventBus();

  onMounted(() => {
    eventBus.on('add-global-toast', (toast: Toast) => addToast(toast));
  });

  onUnmounted(() => {
    eventBus.off('add-global-toast', addToast);
  });
</script>

<style lang="scss" scoped></style>
